import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  
  const loadProfile = useCallback(async (userId) => {
    try {
      
      const { data, error } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Profile load error:', error);
        
        if (error.code === 'PGRST116') { 
          await createDefaultProfile(userId);
          
          const { data: newData } = await supabase
            .from(TABLES.profiles)
            .select('*')
            .eq('id', userId)
            .single();
          
          
          setProfile(prev => {
            if (JSON.stringify(prev) === JSON.stringify(newData)) {
              return prev;
            }
            return newData;
          });
          return;
        }
        
        setProfile(null);
        return;
      }

      
      
      setProfile(prev => {
        if (JSON.stringify(prev) === JSON.stringify(data)) {
          return prev;
        }
        return data;
      });
      
    } catch (error) {
      console.error('Error in loadProfile:', error);
      setProfile(null);
    }
  }, []);

  const createDefaultProfile = async (authUser) => {
    try {
      const { data: { user: authData } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from(TABLES.profiles)
        .insert({
          id: authUser,
          email: authData?.email || '',
          rol: 'elev',
          nume: '',
          prenume: '',
          specializare: null,
          clasa: null,
          telefon: null,
          created_at: new Date().toISOString(),
        });

      if (error && error.code !== '23505') {
        console.error('Error creating default profile:', error);
      }
      
    } catch (error) {
      console.error('Failed to create default profile:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let authSubscription;
    let initTimeout;

    const initAuth = async () => {
      if (!isMounted) return;
      
      setLoading(true);
      
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          if (isMounted) setLoading(false);
          return;
        }
        
        if (isMounted) {
          
          setUser(prev => {
            const newUser = session?.user || null;
            if (prev?.id === newUser?.id) return prev;
            return newUser;
          });
        }
        
        if (session?.user && isMounted) {
          await loadProfile(session.user.id);
          const userData = {
            id: session.user.id,
            email: session.user.email,
          };
          localStorage.setItem('supabase.auth.user', JSON.stringify(userData));
        } else if (isMounted) {
          setProfile(null);
          localStorage.removeItem('supabase.auth.user');
          localStorage.removeItem('supabase.auth.profile');
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    
    initTimeout = setTimeout(() => {
      initAuth();
    }, 100);

    
    let authChangeTimeout;
    
    authSubscription = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        
        if (event === 'PASSWORD_RECOVERY') {
          window.location.href = '/update-password';
          return;
        }
        
        clearTimeout(authChangeTimeout);
        
        authChangeTimeout = setTimeout(async () => {
          if (!isMounted) return;
          
          
          
          setUser(prevUser => {
            const newUser = session?.user || null;
            if (prevUser?.id === newUser?.id) {
              return prevUser;
            }
            return newUser;
          });
          
          if (session?.user) {
            await loadProfile(session.user.id);
          } else {
            if (isMounted) {
              setProfile(null);
            }
          }
          
          if (isMounted) {
            setLoading(false);
          }
        }, 500); 
      }
    );

    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        
        
        const checkAuth = async () => {
          if (!isMounted) return;
          
          try {
            const { data: { session } } = await supabase.auth.getSession();
            
            
            if (isMounted) {
              setUser(prev => {
                const newUser = session?.user || null;
                if (prev?.id === newUser?.id) {
                  return prev;
                }
                return newUser;
              });
              
              if (session?.user) {
                await loadProfile(session.user.id);
              }
            }
          } catch (error) {
            console.error('Error checking auth on visibility change:', error);
          }
        };
        
        
        setTimeout(checkAuth, 1000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    
    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      clearTimeout(authChangeTimeout);
      
      if (authSubscription?.subscription) {
        authSubscription.subscription.unsubscribe();
      }
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadProfile]);

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        await loadProfile(data.user.id);
      }
      
      return data;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, userData) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nume: userData.nume,
            prenume: userData.prenume,
            rol: userData.rol || 'elev',
          },
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      
      if (error) throw error;
      
      if (data.user) {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const { error: profileError } = await supabase
          .from(TABLES.profiles)
          .insert({
            id: data.user.id,
            email: data.user.email,
            rol: userData.rol || 'elev',
            nume: userData.nume || '',
            prenume: userData.prenume || '',
            specializare: userData.specializare || null,
            clasa: userData.clasa || null,
            telefon: userData.telefon || null,
          });

        if (profileError && profileError.code !== '23505') {
          console.error('Profile creation error:', profileError);
        }
        
        await loadProfile(data.user.id);
      }
      
      return data;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      localStorage.removeItem('supabase.auth.user');
      localStorage.removeItem('supabase.auth.profile');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');
    
    const { data, error } = await supabase
      .from(TABLES.profiles)
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    
    if (error) throw error;
    
    setProfile(data);
    return data;
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    initialized, 
  };

  useEffect(() => {
  const loadFromStorage = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        setUser(session.user);
        await loadProfile(session.user.id);
      } else {
        localStorage.removeItem('supabase.auth.user');
        localStorage.removeItem('supabase.auth.profile');
        setUser(null);
        setProfile(null);
      }
    } catch (error) {
      console.error('Error loading from storage:', error);
      localStorage.removeItem('supabase.auth.user');
      localStorage.removeItem('supabase.auth.profile');
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };
  
    loadFromStorage();
  }, [loadProfile]);

  useEffect(() => {
    if (user) {
      const userData = {
        id: user.id,
        email: user.email,
      };
      localStorage.setItem('supabase.auth.user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('supabase.auth.user');
    }
  }, [user]);

  useEffect(() => {
    if (profile) {
      localStorage.setItem('supabase.auth.profile', JSON.stringify(profile));
    } else {
      localStorage.removeItem('supabase.auth.profile');
    }
  }, [profile]);

  if (loading && !initialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        flexDirection: 'column'
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se încarcă autentificarea...
        </Typography>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};